import { FC } from "react";
import { useTranslation } from "react-i18next";

import { ProductCardChip } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardChip";
import { ProductCardFooterItem } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { DeviceProductCard } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/DeviceProductCard.tsx";
import { getIconForProductId } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { useActivatePurchaseIntent } from "@/hooks/useActivatePurchaseIntent.ts";
import { useAlert } from "@/hooks/useAlert.ts";
import {
  BasketSummaryResponse,
  BasketSummaryResponseSimType,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms.ts";
import {
  getFlatHomeBaseDisplayValue,
  TransformedProducts,
} from "@/utils/tariffUtils.ts";

/** What differs between a physical SIM (on its way) and an eSIM intent. */
const intentConfig = {
  physical: {
    dataTestid: "physical-sim-intent",
    defaultTitleKey: "cockpit.purchaseIntent.physical.defaultAlias",
    chipTone: "info",
    chipLabelKey: "cockpit.purchaseIntent.status.onTheWay",
    messageKey: "cockpit.purchaseIntent.physical.message",
    buttonKey: "cockpit.purchaseIntent.physical.button",
  },
  esim: {
    dataTestid: "esim-intent",
    defaultTitleKey: "translation:Common.products.esim",
    chipTone: "warning",
    chipLabelKey: "common.status.inactive",
    messageKey: "cockpit.purchaseIntent.esim.message",
    buttonKey: "cockpit.purchaseIntent.esim.button",
  },
} as const;

interface PurchaseIntentTileProps {
  intent: BasketSummaryResponse;
  processedOptions?: TransformedProducts;
}

/**
 * Tile for a purchase intent: a physical SIM (triple SIM) or an eSIM.
 */
export const PurchaseIntentTile: FC<PurchaseIntentTileProps> = ({
  intent,
  processedOptions,
}) => {
  const { t } = useTranslation(["portal", "translation"]);
  const showAlert = useAlert();
  const { activateIntent, isActivating } = useActivatePurchaseIntent();

  const config =
    intentConfig[
      intent.simType === BasketSummaryResponseSimType.TRIPLESIM
        ? "physical"
        : "esim"
    ];
  const product =
    intent.productId && processedOptions
      ? processedOptions.processedProductsById[intent.productId]
      : null;

  const icon = getIconForProductId(intent.productId);
  const subtitle =
    product?.baseDisplayValue ??
    getFlatHomeBaseDisplayValue(intent.productId) ??
    "-";

  return (
    <DeviceProductCard
      dataTestid={config.dataTestid}
      icon={icon}
      title={intent.simAlias || t(config.defaultTitleKey)}
      subtitle={subtitle}
      chips={
        <ProductCardChip
          tone={config.chipTone}
          label={t(config.chipLabelKey)}
        />
      }
      footer={
        <ProductCardFooterItem
          variant="text-button"
          description={t(config.messageKey)}
          label={t(config.buttonKey)}
          loading={isActivating}
          disabled={isActivating}
          onClick={async () => {
            try {
              await activateIntent(intent);
            } catch (error) {
              showAlert({
                type: AlertTypes.error,
                text: t("Common.generalForm.error.genericError"),
              });
            }
          }}
        />
      }
    />
  );
};
